/**
 * System Architecture section — components, interfaces, data flows
 * (basic + enhanced), system boundary, architecture patterns,
 * technology stack, integration points, dependencies. Pulls from
 * `formatted.system_architecture`.
 *
 * Always renders all SubSections (matches legacy
 * CompleteItemDefinition behavior). Empty tables fall back to a
 * muted "No items." hint.
 */
import React from 'react';
import { SectionCard, SubSection, Field, StringListField, ObjectListField, type SectionProps, type ColumnDescriptor } from './parts';
import { ConfidenceBadge } from './ExtractionQuality';

const PATH = ['system_architecture'];

// Accept real booleans and legacy stringified booleans (the old text-input edit
// path could persist "true"/"True"), so a set value never renders as an em-dash.
const yesNo = (v) => {
  if (v === true || v === 'true' || v === 'True') return 'true';
  if (v === false || v === 'false' || v === 'False') return 'false';
  return '';
};
const joinList = (v) => (Array.isArray(v) ? v.join(', ') : v);

const SENSITIVITY_OPTIONS = ['public', 'internal', 'confidential', 'restricted', 'pii'];
// Mirrors DataFlowFrequency in backend/src/models/item_definition_models.py
const FREQUENCY_OPTIONS = ['continuous', 'periodic', 'on_event', 'on_demand', 'rare'];

export default function SystemArchitecture({ data, isEditing, onChange }: SectionProps) {
  const arch = data ?? {};
  const components = arch.components?.list ?? [];
  const dataFlows = arch.data_flows?.basic ?? [];
  const enhancedFlows = arch.data_flows?.enhanced ?? [];

  const componentColumns: ColumnDescriptor[] = [
    { key: 'id', label: 'ID' },
    { key: 'name', label: 'Name' },
    { key: 'type', label: 'Type' },
    { key: 'category', label: 'Category' },
    { key: 'processes_data', label: 'Processes Data', type: 'boolean', render: yesNo },
    { key: 'external_facing', label: 'External Facing', type: 'boolean', render: yesNo },
    { key: 'security_relevant', label: 'Security Relevant', type: 'boolean', render: yesNo },
    { key: 'update_mechanism', label: 'Update Mechanism', type: 'boolean', render: yesNo },
    // Extraction confidence per component (E9-R1). Matters most when the
    // definition was seeded from a SysML diagram, where the vision pass is
    // interpreting a picture rather than parsing a model.
    { key: 'confidence', label: 'Extraction Confidence', render: (v: unknown) => <ConfidenceBadge value={v} /> },
  ];

  const dataFlowColumns: ColumnDescriptor[] = [
    { key: 'name', label: 'Name' },
    { key: 'source', label: 'Source' },
    { key: 'destination', label: 'Destination' },
    { key: 'data_sensitivity', label: 'Sensitivity', type: 'select', options: SENSITIVITY_OPTIONS },
    { key: 'frequency', label: 'Frequency', type: 'select', options: FREQUENCY_OPTIONS },
  ];

  const enhancedFlowColumns: ColumnDescriptor[] = [
    { key: 'source', label: 'Source' },
    { key: 'destination', label: 'Destination' },
    { key: 'data_description', label: 'Description' },
    { key: 'data_classification', label: 'Classification', render: joinList },
    { key: 'data_sensitivity', label: 'Sensitivity', type: 'select', options: SENSITIVITY_OPTIONS },
    { key: 'data_retention_period', label: 'Retention' },
    { key: 'encryption_in_transit', label: 'Encryption in Transit', type: 'boolean', render: yesNo },
    { key: 'encryption_at_rest', label: 'Encryption at Rest', type: 'boolean', render: yesNo },
  ];

  return (
    <SectionCard title="System Architecture" id="system_architecture">
      <SubSection title="Components" count={components.length}>
        <ObjectListField columns={componentColumns} rows={components} isEditing={isEditing} onChange={onChange} path={[...PATH, 'components', 'list']} />
      </SubSection>

      <SubSection title="Data flows (basic)" count={dataFlows.length}>
        <ObjectListField columns={dataFlowColumns} rows={dataFlows} isEditing={isEditing} onChange={onChange} path={[...PATH, 'data_flows', 'basic']} />
      </SubSection>

      <SubSection title="Data flows (enhanced)" count={enhancedFlows.length}>
        <ObjectListField columns={enhancedFlowColumns} rows={enhancedFlows} isEditing={isEditing} onChange={onChange} path={[...PATH, 'data_flows', 'enhanced']} />
      </SubSection>

      <SubSection title="System boundary">
        <StringListField label="Internal components" items={arch.system_boundary?.internal_components} isEditing={isEditing} onChange={onChange} path={[...PATH, 'system_boundary', 'internal_components']} />
        <StringListField label="External systems" items={arch.system_boundary?.external_systems} isEditing={isEditing} onChange={onChange} path={[...PATH, 'system_boundary', 'external_systems']} />
        <StringListField label="Boundary interfaces" items={arch.system_boundary?.boundary_interfaces} isEditing={isEditing} onChange={onChange} path={[...PATH, 'system_boundary', 'boundary_interfaces']} />
        <StringListField label="Trust boundaries" items={arch.system_boundary?.trust_boundaries} isEditing={isEditing} onChange={onChange} path={[...PATH, 'system_boundary', 'trust_boundaries']} />
        <Field label="Boundary clarity" value={arch.system_boundary?.boundary_clarity} isEditing={isEditing} onChange={onChange} path={[...PATH, 'system_boundary', 'boundary_clarity']} />
        <StringListField label="Analysis notes" items={arch.system_boundary?.analysis_notes} isEditing={isEditing} onChange={onChange} path={[...PATH, 'system_boundary', 'analysis_notes']} />
      </SubSection>

      <SubSection title="Architecture & patterns">
        <StringListField label="Architecture patterns" items={arch.architecture_patterns} isEditing={isEditing} onChange={onChange} path={[...PATH, 'architecture_patterns']} />
        <StringListField label="Technology stack" items={arch.technology_stack} isEditing={isEditing} onChange={onChange} path={[...PATH, 'technology_stack']} />
        <StringListField label="Integration points" items={arch.integration_points} isEditing={isEditing} onChange={onChange} path={[...PATH, 'integration_points']} />
      </SubSection>

      <SubSection title="Dependencies">
        <StringListField label="System-level" items={arch.dependencies?.system_level} isEditing={isEditing} onChange={onChange} path={[...PATH, 'dependencies', 'system_level']} />
      </SubSection>
    </SectionCard>
  );
}
